import type { SalesMetricBasis } from '@plato/app-meta/sales-metric';
import { DoubleDeckerCell } from '@plato/ui-legacy/components/list-views/double-decker-cell';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ExplorerGetForSalesRepItem } from '../../../server/types/infer-trpc-types.ts';
import type {
    CustomDateRange,
    ExplorerSort,
    ExplorerViewFilters,
    VisibilityWindow,
} from '../../../server/utils/explorer-utils.ts';
import { api } from '../../../utils/api.ts';
import {
    useConfigurableExplorerColumns,
    type ConfigurableColumnDefinition,
    type ExplorerTableData,
} from '../hooks/use-configurable-explorer-columns.tsx';
import { useExplorerSorting } from '../hooks/use-explorer-sorting.tsx';
import useRowSelection from '../hooks/use-manage-explorer-filters.tsx';
import { usePreventExplorerUnnecissearyRerender } from '../hooks/use-prevent-explorer-unnecisseary-rerender.ts';
import { ExplorerDataTable } from './explorer-data-table.tsx';

type Props = {
    hideUnselectedRows?: boolean;
    searchString?: string;
    window: VisibilityWindow;
    customDateRange: CustomDateRange | undefined;
    filters: ExplorerViewFilters;
    billingStatus: SalesMetricBasis;
    onFiltersChange: (filters: string[]) => void;
    sortingFilter: ExplorerSort | null;
    setSortingFilter: (sortingFilter: ExplorerSort | null) => void;
    yearPeriods: number;
};

export function ExplorerViewSalesReps({
    hideUnselectedRows = false,
    searchString,
    window,
    customDateRange,
    filters,
    billingStatus,
    onFiltersChange,
    sortingFilter,
    setSortingFilter,
    yearPeriods,
}: Props) {
    const { sorting, sortChangedInInterface } = useExplorerSorting(sortingFilter, setSortingFilter);

    const { filtersWithRerenderProtection } = usePreventExplorerUnnecissearyRerender(
        filters,
        'salesRep',
        hideUnselectedRows,
    );

    const {
        data: dataRaw,
        isLoading,
        isFetching,
        isFetchingNextPage,
        hasNextPage,
        fetchNextPage,
    } = api.platoUser.query.explorer.getForSalesRep.getListWithFilters.useInfiniteQuery(
        {
            searchString,
            hideUnselectedRows,
            filters: filtersWithRerenderProtection,
            customDateRange,
            billingStatus,
            window,
            sorting: sortingFilter,
            yearPeriods,
        },
        {
            getNextPageParam: (lastPage) => lastPage.nextCursor,
        },
    );

    const tabColumns = useExplorerSalesRepTabColumns();
    const columns = useConfigurableExplorerColumns(tabColumns);

    const data = useMemo(() => {
        return dataRaw ? dataRaw.pages.flatMap((page) => page.result) : [];
    }, [dataRaw]);

    const { rowSelection, setRowSelection } = useRowSelection<ExplorerGetForSalesRepItem, 'id'>(
        data,
        'id',
        onFiltersChange,
        filters.salesRep,
    );

    return (
        <ExplorerDataTable
            data={data}
            columns={columns}
            fetchNextPage={fetchNextPage}
            isFetchingNextPage={isFetchingNextPage}
            isFetching={isFetching}
            hasNextPage={hasNextPage}
            isLoading={isLoading}
            filters={filters}
            window={window}
            billingStatus={billingStatus}
            customDateRange={customDateRange}
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
            sorting={sorting}
            sortChangedInInterface={sortChangedInInterface}
        />
    );
}

export function useExplorerSalesRepTabColumns(): ConfigurableColumnDefinition<ExplorerTableData> {
    const { t } = useTranslation();

    return useMemo(
        () => ({
            columns: [
                {
                    id: 'name',
                    accessorKey: 'name',
                    headerText: t('header_tab__SALES_REP', {
                        ns: 'explorer',
                    }),
                    meta: { aiChat: { label: t('header_tab__SALES_REP', { ns: 'explorer' }) } },
                    generateCell: (row) => (
                        <DoubleDeckerCell align="left" topLevel={row.id} bottomLevel={row.name} />
                    ),
                    size: 200,
                },
            ],
        }),
        [t],
    );
}
